/* eslint-disable react/button-has-type, jsx-a11y/label-has-associated-control */
/* eslint-disable no-nested-ternary, jsx-a11y/no-autofocus, react/no-unescaped-entities */
/* eslint-disable no-unused-vars, no-useless-return, curly, nonblock-statement-body-position */
/* eslint-disable no-confusing-arrow */

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { mdiChevronLeft } from '@mdi/js';
import Icon from '@mdi/react';
import { toast as notify } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  INIT,
  MF,
  DEFAULT_OPTIONS,
  SAMPLE,
  SUB_SCREENS,
  WEEKDAYS,
} from './constants';
import DependentsScreen from './components/Modals/DependentsScreen';
import DocumentsScreen from './components/Modals/DocumentsScreen';
import AddressScreen from './components/Modals/AddressScreen';
import FacilitiesScreen from './components/Modals/FacilitiesScreen';
import CompensationScreen from './components/Modals/CompensationScreen';
import CostAllocationScreen from './components/Modals/CostAllocationScreen';
import AttachmentsScreen from './components/Modals/AttachmentsScreen';
import {
  F,
  Section,
  CloseIcon,
  EditIcon,
  DeleteIcon,
  TableActions,
  ConfirmModal,
  Toast,
  Loader,
  SaveIcon,
} from './components/CommonUI';
import PersonalDataTab from './components/Tabs/PersonalDataTab';
import OfficialDetailsTab from './components/Tabs/OfficialDetailsTab';
import ContactOtherTab from './components/Tabs/ContactOtherTab';
import SearchModal from './components/Modals/SearchModal';
import { useDropdowns } from './hooks/useDropdowns';
import {
  apiClient,
  getCompanyCode,
  getEntityCode,
} from '../../config/axios.config';
import { BASE_EMPLOYEE_URL } from '../../store/apiConfig';
import { getEmployeeFilePath } from '../../utils/imageFile.util';
import { fetchDelegateApprovalAutoComplete } from '../../store/apis/autocompleteService';
import './EmployeeMaster.css';

/**
 * Employee Management System
 *
 * A comprehensive HRMS employee management interface with:
 * - 3 main tabs: Personal Data, Official Details, Contact & Other Details
 * - 6 sub-screens: Compensation, Cost Allocation, Documents, Dependents, Address, Facilities
 * - Full CRUD operations: Create, Search, Edit, Delete employees
 * - Mandatory field validation with visual indicators
 * - Responsive design with tablet and mobile breakpoints
 */

// ────────────────────────────────────────────────────────────────
// CONSTANTS & CONFIGURATION (MOVED TO constants.jsx)
// ────────────────────────────────────────────────────────────────

// Reusable UI components moved to CommonUI.jsx

/* ────────────────────────────────────────────────────────────────
   TAB 1 — PERSONAL DATA
   Sections: Employee Identity → Personal Information →
             Designation & Position → Organization
   ──────────────────────────────────────────────────────────────── */
// Tab1: Personal Data component (MOVED TO PersonalDataTab.jsx)

/* ────────────────────────────────────────────────────────────────
   TAB 2 — OFFICIAL DETAILS
   Row 1: Bank Details | Payment Details | Dates+Accruals | Travelling
   Row 2: Separation | Annual Leave | Bonus
   ──────────────────────────────────────────────────────────────── */
// Tab2: Official Details component (MOVED TO OfficialDetailsTab.jsx)

// Tab3: Contact & Other Details component (MOVED TO ContactOtherTab.jsx)

/* ────────────────────────────────────────────────────────────────
   SHARED MODALS — Search, Confirm Delete, Toast
   ──────────────────────────────────────────────────────────────── */
// SearchModal component (MOVED TO Modals/SearchModal.jsx)

/* ────────────────────────────────────────────────────────────────
   SUB-SCREEN ROUTER — routes screen key to the correct component
   ──────────────────────────────────────────────────────────────── */
function SubScreen({
  screen,
  onClose,
  empName,
  empCode,
  opts,
  fetchFacilityCategories,
  readOnly = false,
}) {
  const props = {
    empName,
    empCode,
    onClose,
    opts,
    fetchFacilityCategories,
    readOnly,
  };
  switch (screen) {
    case 'dependents':
      return <DependentsScreen {...props} />;
    case 'documents':
      return <DocumentsScreen {...props} />;
    case 'address':
      return <AddressScreen {...props} />;
    case 'facilities':
      return <FacilitiesScreen {...props} />;
    case 'compensation':
      return <CompensationScreen {...props} />;
    case 'costAllocation':
      return <CostAllocationScreen {...props} />;
    case 'attachments':
      return <AttachmentsScreen {...props} />;
    default:
      return null;
  }
}

const EMP_CODE_DUP_CHECK_MS = 500;

function getEmployeeRowsFromResponse(resp) {
  if (!resp) return [];
  if (Array.isArray(resp.result)) return resp.result;
  if (Array.isArray(resp)) return resp;
  return [];
}

/** True when get-employee returned at least one row (employee exists). */
function employeeReturnedFromGetEmployee(resp) {
  const rows = getEmployeeRowsFromResponse(resp);
  const row = rows[0];
  return !!(row && typeof row === 'object' && Object.keys(row).length > 0);
}

function getEmployeeLookupBody(empCdTrim) {
  return { v_Cd: empCdTrim, employeeId: empCdTrim };
}

function dataUrlToFile(dataUrl, fileName) {
  if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
    return null;
  }

  const [meta, base64Data] = dataUrl.split(',');
  if (!meta || !base64Data) return null;

  const mimeMatch = meta.match(/data:(.*?);base64/);
  const mimeType = mimeMatch?.[1] || 'application/octet-stream';
  const binary = atob(base64Data);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new File([bytes], fileName, { type: mimeType });
}

function isDataUrlValue(value) {
  return typeof value === 'string' && value.startsWith('data:');
}

/** imageFile/imageSign: server path when display value is not a data URL. */
function storedPathForImageField(displayValue, pathFromServer) {
  if (!isDataUrlValue(displayValue)) {
    return String(displayValue ?? '').trim();
  }
  return String(pathFromServer ?? '').trim();
}

function buildEmployeeFormData(payload, emp, options = {}) {
  const { attachPhotoAsFile = false, attachSignAsFile = false } = options;
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (isDataUrlValue(value)) return;
    formData.append(key, value ?? '');
  });

  // Replaced with File blobs for data URLs; never put base64 in text fields.
  formData.append('imageFileUpload', '');
  formData.append('imageSignUpload', '');

  const code = String(payload.cd || emp.empCode || 'employee').trim();
  const photoFile = attachPhotoAsFile
    ? dataUrlToFile(emp.photo, `${code}-photo.jpg`)
    : null;
  const signFile = attachSignAsFile
    ? dataUrlToFile(emp.signature, `${code}-signature.jpg`)
    : null;

  if (photoFile) {
    formData.set('imageFileUpload', photoFile);
  }
  if (signFile) {
    formData.set('imageSignUpload', signFile);
  }

  return formData;
}

function buildWeeklyHolidayFromDefaultIndexes(defaultIndexes) {
  const weeklyHoliday = JSON.parse(JSON.stringify(INIT.weeklyHoliday));
  if (!Array.isArray(defaultIndexes) || !defaultIndexes.length) {
    return weeklyHoliday;
  }

  WEEKDAYS.forEach((day, idx) => {
    weeklyHoliday[day] = { f: defaultIndexes.includes(idx), h: false };
  });

  return weeklyHoliday;
}

/* ────────────────────────────────────────────────────────────────
   MAIN APPLICATION
   State: tab, mode (view/create/edit), employee data, employee list
   ──────────────────────────────────────────────────────────────── */
export default function EmployeeMaster() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showBackButton = useMemo(() => {
    const params = [...searchParams.entries()].filter(
      ([, value]) => String(value || '').trim() !== '',
    );
    if (params.length !== 1) return false;
    const [paramKey, paramValue] = params[0];
    return (
      String(paramKey || '').toLowerCase() === 'empcode' &&
      Boolean(String(paramValue || '').trim())
    );
  }, [searchParams]);
  const hasEmployeeIdInUrl = useMemo(() => {
    const employeeId =
      searchParams.get('EmpCode') || searchParams.get('EmployeeId') || '';
    return Boolean(employeeId.trim());
  }, [searchParams]);
  /** Deep link (?EmpCode=…): view-only; main CRUD + sub-screen mutations disabled. */
  const isReadOnlyDeepLink = useMemo(() => {
    const code =
      searchParams.get('EmpCode') || searchParams.get('empCode') || '';
    return Boolean(code.trim());
  }, [searchParams]);
  const {
    options: opts,
    fetchBankBranches,
    fetchRelation,
    fetchFacilityTypes,
    fetchFacilityCategories,
    fetchAttachmentTypes,
    fetchDocumentTypes,
    fetchDependents,
  } = useDropdowns();
  const [tab, setTab] = useState(0);
  const [mode, setMode] = useState('view');
  const [emp, setEmp] = useState({ ...INIT });
  /** Post 1 = submitted, 2 = approved — same view-only rules as deep link. */
  const isPostSubmittedOrApproved = ['1', '2'].includes(
    String(emp.postStatus ?? '').trim(),
  );
  const isSubmittedPost = String(emp.postStatus ?? '').trim() === '1';
  const isReadOnly = isReadOnlyDeepLink || isPostSubmittedOrApproved;
  const [list, setList] = useState(SAMPLE);
  const [search, setSearch] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [subScreen, setSubScreen] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingAction, setLoadingAction] = useState('fetch');
  const [isDeleting, setIsDeleting] = useState(false);
  const [editBaselineEmp, setEditBaselineEmp] = useState(null);
  const moreRef = useRef();
  const empCodeDupCheckTimerRef = useRef(null);
  const lastDupToastEmpCodeRef = useRef('');
  const pickEmployeeRef = useRef(null);

  useEffect(() => {
    if (toast?.type !== 'success') return;
    notify.success(toast.message, {
      className: 'toast-success',
      position: 'top-center',
    });
    setToast(null);
  }, [toast]);

  useEffect(() => {
    const handler = e => {
      if (moreRef.current && !moreRef.current.contains(e.target))
        setShowMore(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (mode !== 'create') {
      lastDupToastEmpCodeRef.current = '';
      return undefined;
    }
    const code = String(emp.empCode || '').trim();
    if (!code) {
      lastDupToastEmpCodeRef.current = '';
      return undefined;
    }

    if (empCodeDupCheckTimerRef.current)
      clearTimeout(empCodeDupCheckTimerRef.current);
    empCodeDupCheckTimerRef.current = setTimeout(async () => {
      empCodeDupCheckTimerRef.current = null;
      try {
        const dupResp = await apiClient.create(
          `${BASE_EMPLOYEE_URL}/get-employee`,
          getEmployeeLookupBody(code),
        );
        if (employeeReturnedFromGetEmployee(dupResp)) {
          if (lastDupToastEmpCodeRef.current !== code) {
            lastDupToastEmpCodeRef.current = code;
            setToast({
              message:
                'This Emp Code already exists. Enter a different Emp Code or search for the employee.',
              type: 'error',
            });
          }
        } else {
          lastDupToastEmpCodeRef.current = '';
        }
      } catch {
        /* optional check while typing — ignore network errors */
      }
    }, EMP_CODE_DUP_CHECK_MS);

    return () => {
      if (empCodeDupCheckTimerRef.current) {
        clearTimeout(empCodeDupCheckTimerRef.current);
        empCodeDupCheckTimerRef.current = null;
      }
    };
  }, [emp.empCode, mode]);

  const off = mode === 'view' || isReadOnly;
  const ch = useCallback(
    (n, v) => {
      if (isReadOnly) return;
      setEmp(p => ({ ...p, [n]: v }));
      if (n === 'bankCode') fetchBankBranches(v, emp.empCode);
    },
    [emp.empCode, fetchBankBranches, isReadOnly],
  );
  const tabs = ['Personal Data', 'Official Details', 'Contact & Other Details'];
  const isMoreActionsDisabled =
    mode === 'create' || !String(emp.empCode || '').trim();

  const getPostStatusLabel = status => {
    switch (status) {
      case '0':
        return 'Draft';
      case '1':
        return 'Pending for Approval';
      case '2':
        return 'Approved';
      default:
        return '';
    }
  };

  const LABEL_MAP = {
    // Tab 0: Personal Data
    empCode: 'Emp Code',
    shortName: 'Short Name',
    firstName: 'First Name',
    // middleName: 'Middle Name',
    lastName: 'Last Name',
    status: 'Status',
    dateOfBirth: 'Date of Birth',
    // gender: 'Gender',
    maritalStatus: 'Marital Status',
    nationality: 'Nationality',
    religion: 'Religion',
    // bloodGroup: 'Blood Group',

    // Designation & Organization
    desgInternal: 'Desg (Internal)',
    desgExternal: 'Desg (External)',
    // desgVisa: 'Desg (Visa)',
    apprGroup: 'Appr. Group',
    classification: 'Classification-1',
    classification2: 'Classification-2',
    entity: 'Entity',
    costCentre: 'Cost Centre',
    department: 'Department',
    section: 'Section',
    location: 'Location',
    holidayGrp: 'Holiday Group',
    reporting1: 'Reporting-1',
    reporting2: 'Reporting-2',
    deptMgr: 'Dept. Manager',

    // Tab 1: Official Details
    payMode: 'Pay Mode',
    bankCode: 'Bank Code',
    bankBranch: 'Bank Branch',
    ibanAcNo: 'IBAN/Ac No.',
    acName: 'Ac Name',
    payCurrency: 'Pay Currency',
    payGroup: 'Pay Group',
    wpsNo: 'WPS No.',
    financeCode: 'Finance Code',
    otEligibility: 'OT Eligibility',

    // Dates & Accruals
    hireDate: 'Hire Date',
    reHireDate: 'ReHire Date',
    validity: 'Validity Date',
    indemnity: 'Indemnity',
    leaveFare: 'Leave Fare',
    leaveSalary: 'Leave Salary',
    bonus: 'Bonus',

    // Travel
    travelEligibility: 'Travel Eligibility',
    ticketsContract: 'Tickets (Contract)',
    ticketsActual: 'Tickets (Actual)',
    travelSector: 'Travel Sector',
    travelClass: 'Travel Class',

    // Termination & Leave
    accrualType: 'Accrual Type',
    noticeDays: 'Notice Days',
    annualLeaveDays: 'Annual Leave Days',
    carryOver: 'Carry Over Days',
    bonusScheme: 'Bonus Scheme',
    pcmScheme: 'PCM Scheme',

    // Tab 2: Contact & Identity
    passportNo: 'Passport No.',
    issuePlace: 'Issue Place',
    issueDate: 'Issue Date',
    expiryDate: 'Expiry Date',
    localMobile: 'Local Mobile',
    outstationMobile: 'Outstation Mobile',
    email: 'Email Account',
    arabicName: 'Arabic Name',
    sponsor: 'Sponsor',
  };

  const FIELD_ORDER = Object.keys(LABEL_MAP);

  const FIELD_TABS = {
    // Tab 0: Personal Data
    empCode: 0,
    shortName: 0,
    firstName: 0,
    middleName: 0,
    lastName: 0,
    status: 0,
    dateOfBirth: 0,
    gender: 0,
    maritalStatus: 0,
    nationality: 0,
    religion: 0,
    bloodGroup: 0,
    desgInternal: 0,
    desgExternal: 0,
    desgVisa: 0,
    apprGroup: 0,
    classification: 0,
    classification2: 0,
    entity: 0,
    costCentre: 0,
    department: 0,
    section: 0,
    location: 0,
    holidayGrp: 0,
    reporting1: 0,
    reporting2: 0,
    deptMgr: 0,

    // Tab 1: Official Details
    payMode: 1,
    bankCode: 1,
    bankBranch: 1,
    ibanAcNo: 1,
    acName: 1,
    payCurrency: 1,
    payGroup: 1,
    wpsNo: 1,
    financeCode: 1,
    otEligibility: 1,
    hireDate: 1,
    reHireDate: 1,
    validity: 1,
    indemnity: 1,
    leaveFare: 1,
    leaveSalary: 1,
    bonus: 1,
    travelEligibility: 1,
    ticketsContract: 1,
    ticketsActual: 1,
    travelSector: 1,
    travelClass: 1,
    accrualType: 1,
    noticeDays: 1,
    annualLeaveDays: 1,
    carryOver: 1,
    bonusScheme: 1,
    pcmScheme: 1,

    // Tab 2: Contact & Others
    passportNo: 2,
    issuePlace: 2,
    issueDate: 2,
    expiryDate: 2,
    localMobile: 2,
    outstationMobile: 2,
    email: 2,
    arabicName: 2,
    sponsor: 2,
    weeklyHoliday: 2,
  };

  const validate = () => {
    const isEmptyValue = value =>
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '');
    const missingKeys = [...MF].filter(f => isEmptyValue(emp[f]));
    const isBank =
      emp.payMode === 'HPMOD0001' ||
      (emp.payMode && String(emp.payMode).toLowerCase().includes('bank'));
    if (isBank) {
      ['bankCode', 'bankBranch', 'ibanAcNo', 'acName', 'payGroup'].forEach(
        f => {
          if (isEmptyValue(emp[f]) && !missingKeys.includes(f))
            missingKeys.push(f);
        },
      );
    }
    if (emp.travelEligibility === 'Yes') {
      [
        'ticketsContract',
        'ticketsActual',
        'travelSector',
        'travelClass',
      ].forEach(f => {
        if (isEmptyValue(emp[f]) && !missingKeys.includes(f))
          missingKeys.push(f);
      });
    }

    if (missingKeys.length) {
      // Sort missing keys by visual order
      missingKeys.sort((a, b) => {
        const idxA = FIELD_ORDER.indexOf(a);
        const idxB = FIELD_ORDER.indexOf(b);
        return (idxA > -1 ? idxA : 999) - (idxB > -1 ? idxB : 999);
      });

      const first = missingKeys[0];
      const targetTab = FIELD_TABS[first] !== undefined ? FIELD_TABS[first] : 1;
      setTab(targetTab);

      setTimeout(() => {
        const el = document.getElementById(`f-${first}`);
        if (el) {
          el.focus();
          el.classList.add('pulse');
          setTimeout(() => el.classList.remove('pulse'), 2000);
        }
      }, 100);

      const missingLabels = missingKeys.map(k => LABEL_MAP[k] || k);
      setToast({
        message: `Please fill mandatory fields: ${missingLabels
          .slice(0, 3)
          .join(', ')}${missingLabels.length > 3 ? '…' : ''}`,
        type: 'error',
      });
      return false;
    }

    return true;
  };

  const resetToCreateForm = useCallback(() => {
    setEmp({
      ...INIT,
      weeklyHoliday: buildWeeklyHolidayFromDefaultIndexes(
        opts.weeklyHolidayDefault,
      ),
      weeklyHolidayApi: buildWeeklyHolidayFromDefaultIndexes(
        opts.weeklyHolidayDefault,
      ),
    });
    setEditBaselineEmp(null);
    setMode('create');
    setTab(0);
    setTimeout(() => {
      const el = document.getElementById('f-empCode');
      if (el) el.focus();
    }, 100);
  }, [opts.weeklyHolidayDefault]);

  const doNew = () => {
    if (isReadOnlyDeepLink) {
      setToast({
        message:
          'This page is view-only. Use Employee Master from the menu to create or edit.',
        type: 'info',
      });
      return;
    }
    resetToCreateForm();
  };
  const saveEmployeeRecord = async ({ silent = false } = {}) => {
    if (isReadOnly) return false;
    if (!validate()) return false;

    try {
      if (mode === 'create') {
        const empCdTrim = String(emp.empCode || '').trim();
        const dupResp = await apiClient.create(
          `${BASE_EMPLOYEE_URL}/get-employee`,
          getEmployeeLookupBody(empCdTrim),
        );
        if (employeeReturnedFromGetEmployee(dupResp)) {
          setToast({
            message:
              'This Emp Code already exists. Enter a different Emp Code or search for the employee.',
            type: 'error',
          });
          setTimeout(() => {
            const el = document.getElementById('f-empCode');
            if (el) el.focus();
          }, 100);
          return false;
        }
      }

      const coCd = localStorage.getItem('entityCode') || '';
      const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
      const userId = authUser.userName || 'ADMIN';
      const weeklyHoliday = WEEKDAYS.map(day => {
        const dayValue = emp.weeklyHoliday?.[day] || {};
        return [dayValue.f ? '1' : '0', dayValue.h ? '1' : '0'];
      })
        .flat()
        .join(',');

      // Map FE state (emp) to BE request (ICreateEmployeeRequest)
      // Data URLs use multipart file parts, not imageFile / imageSign text fields.
      const attachPhotoAsFile = isDataUrlValue(emp.photo);
      const attachSignAsFile = isDataUrlValue(emp.signature);
      const payload = {
        cd: emp.empCode,
        name: emp.shortName,
        fname: emp.firstName,
        mname: emp.middleName,
        lname: emp.lastName,
        status: emp.status, // Default status for employee
        dob: emp.dateOfBirth,
        gender: emp.gender === 'Male' ? 'M' : 'F',
        marital: emp.maritalStatus,
        nat: emp.nationality,
        relg: emp.religion,
        bloodGrp: emp.bloodGroup,
        desg: emp.desgInternal,
        desgExternal: emp.desgExternal,
        desgVisa: emp.desgVisa,
        aprGrp: emp.apprGroup,
        classification1: emp.classification,
        classification2: emp.classification2,
        div: emp.entity,
        cc: emp.costCentre,
        dept: emp.department,
        section: emp.section,
        locCd: emp.location,
        empTyp: emp.holidayGrp,
        repTo: emp.reporting1, // ID Number
        sectionMgr: emp.reporting2, // ID Number
        deptMgr: emp.deptMgr, // ID Number
        payMode: emp.payMode,
        bank: emp.bankCode,
        bankBranch: emp.bankBranch,
        bankAcNo: emp.ibanAcNo,
        bankAcName: emp.acName,
        currCd: emp.payCurrency,
        bankGrp: emp.payGroup,
        wpsNo: emp.wpsNo,
        financeCode: emp.financeCode,
        otEligible: emp.otEligibility === 'Yes' ? 'Y' : 'N',
        doj: emp.hireDate, // Date of Joining
        initialHire: emp.reHireDate,
        validity: emp.validity,
        gt: emp.indemnity ? 'Y' : 'N',
        ls: emp.leaveSalary ? 'Y' : 'N',
        lt: emp.leaveFare ? 'Y' : 'N',
        bn: emp.bonus ? 'Y' : 'N',
        // Fare Eligible should follow travel eligibility radio (Yes/No).
        fareEligible: emp.travelEligibility === 'Yes' ? 'Y' : 'N',
        conTicketCat: emp.ticketsContract,
        ticketCat: emp.ticketsActual,
        travSect: emp.travelSector,
        travClass: emp.travelClass,
        accrualTyp: emp.accrualType,
        noticeDays: emp.noticeDays ? Number(emp.noticeDays) : 0,
        lvCat: emp.annualLeaveDays,
        carryDays: emp.carryOver ? Number(emp.carryOver) : 0,
        bonus: emp.bonusScheme,
        pcm: emp.pcmScheme,
        ppNo: emp.passportNo,
        ppIssuePlace: emp.issuePlace,
        ppIssueDt: emp.issueDate,
        ppExpDt: emp.expiryDate,
        lMobile: emp.localMobile,
        oMobile: emp.outstationMobile,
        email: emp.email,
        arabName: emp.arabicName,
        arabicName: emp.arabicName,
        sponsor: emp.sponsor,
        imageFile: attachPhotoAsFile
          ? ''
          : storedPathForImageField(emp.photo, emp.photoPath),
        imageSign: attachSignAsFile
          ? ''
          : storedPathForImageField(emp.signature, emp.signaturePath),
        weeklyHoliday,
        coCd,
        userId,
      };

      const formDataPayload = buildEmployeeFormData(payload, emp, {
        attachPhotoAsFile,
        attachSignAsFile,
      });
      const response = await apiClient.create(
        `${BASE_EMPLOYEE_URL}/create-employee`,
        formDataPayload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      // Check for success in various common response formats
      const isSuccess =
        response.success ||
        response.status === 'SUCCESS' ||
        response.Code === 0 ||
        (typeof response === 'string' && response.includes('success'));

      if (isSuccess) {
        setEmp(prev => ({
          ...prev,
          weeklyHolidayApi: prev.weeklyHoliday,
        }));
        if (!silent) {
          setToast({
            message:
              mode === 'create'
                ? 'Employee created successfully!'
                : 'Employee updated successfully!',
            type: 'success',
          });
          setMode('view');
          setTab(0);
        }
        return true;
      }
      throw new Error(response.message || 'Failed to save employee');
    } catch (error) {
      setToast({
        message:
          error.message ||
          error.Message ||
          'Failed to save employee details. Please check the network or server logs.',
        type: 'error',
      });
      return false;
    }
  };

  const doSave = async () => {
    if (isReadOnly) return;
    setLoadingAction(mode === 'create' ? 'save' : 'update');
    setLoadingDetails(true);
    try {
      await saveEmployeeRecord();
    } finally {
      setLoadingDetails(false);
    }
  };

  const doSubmit = async () => {
    if (isReadOnly) return;
    if (!emp.empCode || mode !== 'edit' || emp.postStatus !== '0') return;

    setLoadingAction('submit');
    setLoadingDetails(true);
    try {
      const isSaved = await saveEmployeeRecord({ silent: true });
      if (!isSaved) return;

      const validateResp = await apiClient.create(
        `${BASE_EMPLOYEE_URL}/validate-employee-submit`,
        {
          empCd: emp.empCode,
        },
      );
      const validationResult = validateResp?.result || validateResp || {};
      const validationErrors = Array.isArray(validationResult?.errors)
        ? validationResult.errors
        : [];

      if (!validationResult?.isValid) {
        const formattedErrors = validationErrors
          .map(err => String(err || '').trim())
          .filter(Boolean)
          .join('\n');
        setToast({
          message: formattedErrors
            ? `Request cannot be submitted due to below missing information. Please correct the details :\n${formattedErrors}`
            : 'Cannot submit employee. Please complete required sub-screen data.',
          type: 'error',
        });
        return;
      }

      const submitResp = await apiClient.create(
        `${BASE_EMPLOYEE_URL}/submit-employee`,
        {
          empCd: emp.empCode,
        },
      );
      const submitSuccess =
        submitResp?.success ||
        submitResp?.status === 'SUCCESS' ||
        submitResp?.Code === 0 ||
        (typeof submitResp === 'string' &&
          submitResp.toLowerCase().includes('success'));

      if (!submitSuccess) {
        throw new Error(submitResp?.message || 'Submit failed.');
      }

      resetToCreateForm();
      setToast({
        message: 'Employee submitted successfully!',
        type: 'success',
      });
    } catch (error) {
      setToast({
        message: error.message || error.Message || 'Failed to submit employee.',
        type: 'error',
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const doEdit = () => {
    if (isReadOnly) {
      setToast({
        message: isReadOnlyDeepLink
          ? 'This page is view-only. Use Employee Master from the menu to edit.'
          : 'Submitted or approved employees are view-only.',
        type: 'info',
      });
      return;
    }
    if (!emp.empCode) {
      setToast({
        message: 'Search and select an employee to continue.',
        type: 'info',
      });
      return;
    }
    setEditBaselineEmp(JSON.parse(JSON.stringify(emp)));
    setMode('edit');
  };
  const doCancel = () => {
    if (mode === 'edit' && editBaselineEmp) {
      setEmp(JSON.parse(JSON.stringify(editBaselineEmp)));
      return;
    }
    setEmp({
      ...INIT,
      weeklyHoliday: buildWeeklyHolidayFromDefaultIndexes(
        opts.weeklyHolidayDefault,
      ),
      weeklyHolidayApi: buildWeeklyHolidayFromDefaultIndexes(
        opts.weeklyHolidayDefault,
      ),
    });
  };
  const doDelete = async () => {
    if (isReadOnly) return;
    if (!emp.empCode) return;

    setIsDeleting(true);
    try {
      const coCd = getEntityCode() || getCompanyCode() || 'AE01';
      const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
      const userId = authUser.userName || 'ADMIN';

      const payload = {
        employeeId: emp.empCode,
        userId,
        coCd,
      };

      const response = await apiClient.delete(
        `${BASE_EMPLOYEE_URL}/delete-employee`,
        { data: payload },
      );

      // Check for success in common response formats
      const isSuccess =
        response.success ||
        response.status === 'SUCCESS' ||
        response.Code === 0 ||
        (typeof response === 'string' &&
          response.toLowerCase().includes('success'));

      if (isSuccess) {
        setList(p => p.filter(e => e.empCode !== emp.empCode));
        setEmp({ ...INIT });
        setMode('view');
        setConfirm(false);
        setToast({
          message: 'Employee deleted successfully!',
          type: 'success',
        });
      } else {
        throw new Error(response.message || 'Failed to delete employee');
      }
    } catch (error) {
      setToast({
        message:
          error.message || 'Failed to delete employee. Please try again.',
        type: 'error',
      });
    } finally {
      setIsDeleting(false);
      setConfirm(false);
    }
  };
  const openSubScreen = key => {
    setShowMore(false);

    if (mode === 'create' || !String(emp.empCode || '').trim()) {
      setToast({
        message: 'Search and select an employee to continue.',
        type: 'error',
      });
      return;
    }

    // Fetch required codelists when opening sub-screens
    if (key === 'dependents') {
      fetchRelation(emp.empCode);
    } else if (key === 'facilities') {
      fetchFacilityTypes(emp.empCode);
      fetchDependents(emp.empCode);
    } else if (key === 'documents') {
      fetchDocumentTypes(emp.empCode);
    } else if (key === 'attachments') {
      fetchAttachmentTypes(emp.empCode);
    }

    setSubScreen(key);
  };

  const handlePickEmployee = async picked => {
    setSearch(false);
    setLoadingAction('fetch');
    setLoadingDetails(true);
    try {
      const response = await apiClient.create(
        `${BASE_EMPLOYEE_URL}/get-employee`,
        {
          v_Cd: picked.empCode || picked.Code,
        },
      );

      const res = response.result?.[0] || response?.[0] || {};
      if (!res.Code && !res.empCode) {
        setToast({
          message: 'No detailed data found for this employee.',
          type: 'info',
        });
        return;
      }

      // Helper to trim strings from DB
      const t = v => (typeof v === 'string' ? v.trim() : v);
      const parseWeekOff = weekOffValue => {
        const defaultWeeklyHoliday = JSON.parse(
          JSON.stringify(INIT.weeklyHolidayApi),
        );
        if (!weekOffValue) return defaultWeeklyHoliday;

        let weekOffList = [];
        try {
          weekOffList = Array.isArray(weekOffValue)
            ? weekOffValue
            : JSON.parse(String(weekOffValue));
        } catch (error) {
          return defaultWeeklyHoliday;
        }

        if (!Array.isArray(weekOffList)) return defaultWeeklyHoliday;

        const dayMap = {
          fri: 'friday',
          friday: 'friday',
          sat: 'saturday',
          saturday: 'saturday',
          sun: 'sunday',
          sunday: 'sunday',
          mon: 'monday',
          monday: 'monday',
          tue: 'tuesday',
          tues: 'tuesday',
          tuesday: 'tuesday',
          wed: 'wednesday',
          wednesday: 'wednesday',
          thu: 'thursday',
          thur: 'thursday',
          thurs: 'thursday',
          thursday: 'thursday',
        };

        weekOffList.forEach(item => {
          const dayKey =
            dayMap[
              String(item?.Day || '')
                .trim()
                .toLowerCase()
            ];
          const offType = String(item?.OffType || '')
            .trim()
            .toLowerCase();
          if (!dayKey || !defaultWeeklyHoliday[dayKey]) return;

          if (offType === 'full') defaultWeeklyHoliday[dayKey].f = true;
          if (offType === 'half') defaultWeeklyHoliday[dayKey].h = true;
        });

        return defaultWeeklyHoliday;
      };
      const weeklyHolidayFromApi = parseWeekOff(res.WeekOff ?? res.weekOff);

      // Map DB fields to FE state structure (INIT)
      const mapped = {
        ...INIT,
        empCode: t(res.Code) || t(res.empCode) || '',
        shortName: t(res.Name) || t(res.name) || '',
        firstName: t(res.FirstName) || t(res.firstName) || '',
        middleName: t(res.MiddleName) || t(res.middleName) || '',
        lastName: t(res.LastName) || t(res.lastName) || '',
        status: t(res.Status) || t(res.status) || '',
        dateOfBirth: res.DOB ? res.DOB.split('T')[0] : t(res.dateOfBirth) || '',
        gender:
          t(res.Gender) === 'M' || t(res.Sex) === 'M'
            ? 'Male'
            : t(res.Gender) === 'F' || t(res.Sex) === 'F'
              ? 'Female'
              : t(res.Gender) || t(res.Sex) || t(res.gender) || 'Male',
        maritalStatus:
          t(res['Marital Status']) ||
          t(res.Marital) ||
          t(res.maritalStatus) ||
          '',
        nationality:
          t(res.Nationality) || t(res.Nat) || t(res.nationality) || '',
        religion: t(res.Religion) || t(res.Relg) || t(res.religion) || '',
        bloodGroup: t(res.BloodGroup) || t(res.bloodGroup) || '',
        desgInternal:
          t(res.Designation) || t(res.Desg) || t(res.desgInternal) || '',
        desgExternal: t(res.Desg_External) || t(res.desgExternal) || '',
        desgVisa: t(res.Desg_Visa) || t(res.desgVisa) || '',
        apprGroup: t(res.ApprCd) || t(res.apprGroup) || '',
        classification:
          t(res.Classification1) ||
          t(res.Classification) ||
          t(res.classification1) ||
          t(res.classification) ||
          '',
        classification2: t(res.Classification2) || t(res.classification2) || '',
        entity: t(res.Branch) || t(res.Div) || t(res.entity) || '',
        costCentre: t(res.CC) || t(res.costCentre) || '',
        department: t(res.Department) || t(res.Dept) || t(res.department) || '',
        section: t(res.Section) || t(res.section) || '',
        location: t(res.Location) || t(res.LocCd) || t(res.location) || '',
        holidayGrp: t(res['Emp type']) || t(res.EmpTyp) || t(res.empTyp) || '',
        reporting1: t(res.ReportTo) || '',
        reporting1Name: t(res.ReportToName) || '',
        reporting2: t(res.SectionMgr) || '',
        reporting2Name: t(res.SectionMgrName) || '',
        deptMgr: t(res.CostApprover) || t(res.DeptMgr) || '',
        deptMgrName: t(res.DeptMgrName) || t(res.CostApproverName) || '',
        payMode: t(res['Pay Mode']) || t(res.PayMode) || t(res.payMode) || '',
        bankCode: t(res.Bank) || t(res.BankCd) || t(res.bankCode) || '',
        bankBranch: t(res.BankBranch) || t(res.bankBranch) || '',
        ibanAcNo:
          t(res.EmpAc) ||
          t(res.EmpAcNo) ||
          t(res.EmpAcNumber) ||
          t(res.empAc) ||
          t(res.ibanAcNo) ||
          '',
        acName: t(res.EmpAcName) || t(res.empAcName) || t(res.acName) || '',
        payCurrency:
          t(res.Currency) ||
          t(res.PayCurrency) ||
          t(res.CurrCd) ||
          t(res.payCurrency) ||
          'Dirhams',
        payGroup:
          t(res.BankGrp) ||
          t(res.PayGroup) ||
          t(res['Pay Group']) ||
          t(res.payGroup) ||
          '',
        wpsNo: t(res.WPSNo) || t(res.FileNo) || t(res.wpsNo) || '',
        financeCode:
          t(res.FinanceCode) || t(res.GlPostCd) || t(res.financeCode) || '',
        otEligibility:
          (t(res['OT Eligible']) || t(res.OTEligible) || t(res.otEligible)) ===
          'N'
            ? 'No'
            : 'Yes',
        hireDate: res.DOJ ? res.DOJ.split('T')[0] : t(res.hireDate) || '',
        reHireDate: res.InitialHire
          ? res.InitialHire.split('T')[0]
          : t(res.reHireDate) || '',
        validity: res.Validity
          ? res.Validity.split('T')[0]
          : t(res.validity) || '',
        noticeDays: res.NoticeDays ?? res.noticeDays ?? '',
        accrualType:
          t(res.GTType) || t(res.AccrualTyp) || t(res.accrualTyp) || '',
        travelEligibility: res.FareEligiblity === 'N' ? 'No' : 'Yes',
        ticketsContract:
          t(res.ContractTickets) ||
          t(res.ContractTicketCat) ||
          t(res.ConTicketCat) ||
          t(res.conTicketCat) ||
          t(res.ticketsContract) ||
          '',
        ticketsActual:
          t(res['No. of Tickets']) ||
          t(res.TicketCat) ||
          t(res.ticketCat) ||
          t(res.ticketsActual) ||
          '',
        travelSector:
          t(res['Travel Sector']) ||
          t(res.TravSect) ||
          t(res.travSect) ||
          t(res.travelSector) ||
          '',
        travelClass:
          t(res['Travel Class']) ||
          t(res.TravClass) ||
          t(res.travClass) ||
          t(res.travelClass) ||
          '',
        bonusScheme: t(res.Bonus) || t(res.BonusCd) || t(res.bonusScheme) || '',
        pcmScheme: t(res.PCM) || t(res.PCMCd) || t(res.pcmScheme) || '',
        annualLeaveDays:
          t(res['Leave Days']) || t(res.LvCat) || t(res.annualLeaveDays) || '',
        carryOver: t(res.CarryOverDays) || t(res.carryOver) || '',
        passportNo: t(res.PPNo) || t(res.DocNo) || t(res.passportNo) || '',
        issuePlace:
          t(res.PPIssuePlace) || t(res.IssuePlace) || t(res.issuePlace) || '',
        issueDate: res.PPIssueDt
          ? res.PPIssueDt.split('T')[0]
          : t(res.issueDate) || '',
        expiryDate: res.PPExpDt
          ? res.PPExpDt.split('T')[0]
          : t(res.expiryDate) || '',
        localMobile: t(res.LMobile) || t(res.localMobile) || '',
        outstationMobile: t(res.OMobile) || t(res.outstationMobile) || '',
        email: t(res.Email) || t(res.email) || '',
        arabicName:
          t(res.ArabicName) ||
          t(res.ArabName) ||
          t(res.arabicName) ||
          t(res.arabName) ||
          '',
        sponsor: t(res.Sponsor) || t(res.sponsor) || '',
        photoPath:
          t(res.ImageFile) ||
          t(res.Imagefile) ||
          t(res['Image File Path']) ||
          t(res.FullImagePath) ||
          '',
        signaturePath:
          t(res.Signaturefile) ||
          t(res.SignatureFile) ||
          t(res['Signature File Path']) ||
          t(res.FullSignaturePath) ||
          '',
        photo:
          t(res.PhotoBase64) ||
          t(res.photoBase64) ||
          t(res.Photo) ||
          t(res.photo) ||
          t(res.FullImagePath) ||
          t(res['Image File Path']) ||
          '',
        signature:
          t(res.SignatureBase64) ||
          t(res.signatureBase64) ||
          t(res.Signature) ||
          t(res.signature) ||
          t(res.FullSignaturePath) ||
          t(res['Signature File Path']) ||
          '',
        indemnity: res.GT === 'Y',
        leaveSalary: res.LS === 'Y',
        bonus: res.BN === 'Y',
        leaveFare: res.LT === 'Y' || res.FareEligible === 'Y',
        weeklyHoliday: weeklyHolidayFromApi,
        weeklyHolidayApi: weeklyHolidayFromApi,
        postStatus: t(res.Post) || '0',
      };

      // Resolve manager names if they are missing or returned as IDs
      const resolveManagerName = async (id, currentName) => {
        if (!id) return '';
        // If currentName is empty or looks like an ID, try to resolve it
        if (
          !currentName ||
          currentName === id ||
          /^[A-Z]{2,}\d+/.test(currentName)
        ) {
          try {
            const autoRes = await fetchDelegateApprovalAutoComplete({
              prefixText: id,
            });
            const autoList = autoRes.result || autoRes || [];
            const match = autoList.find(
              item =>
                (item.Cd || '').trim() === id || (item.ID || '').trim() === id,
            );
            return match?.Name || currentName || id;
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Error resolving manager name:', err);
            return currentName || id;
          }
        }
        return currentName;
      };

      mapped.reporting1Name = await resolveManagerName(
        mapped.reporting1,
        mapped.reporting1Name,
      );
      mapped.reporting2Name = await resolveManagerName(
        mapped.reporting2,
        mapped.reporting2Name,
      );
      mapped.deptMgrName = await resolveManagerName(
        mapped.deptMgr,
        mapped.deptMgrName,
      );

      setEmp(mapped);
      setEditBaselineEmp(JSON.parse(JSON.stringify(mapped)));

      // Fetch dynamic bank branches for the loaded bank code
      if (mapped.bankCode) {
        fetchBankBranches(mapped.bankCode, mapped.empCode);
      }

      setMode('view');
      // setToast({
      //   message: `Loaded details for ${mapped.firstName}`,
      //   type: 'success',
      // });
    } catch (error) {
      setToast({
        message: 'Could not load employee details from server.',
        type: 'error',
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  pickEmployeeRef.current = handlePickEmployee;

  useEffect(() => {
    const empCode = (
      searchParams.get('EmpCode') ||
      searchParams.get('empCode') ||
      ''
    ).trim();
    if (!empCode) return;

    const coCd = (
      searchParams.get('CoCd') ||
      searchParams.get('coCd') ||
      ''
    ).trim();
    if (coCd) {
      sessionStorage.setItem('companyCode', coCd);
    }

    pickEmployeeRef.current?.({ empCode })?.catch(() => {});
  }, [searchParams]);

  return (
    <div className="page-content" style={{ padding: '85px 0 0 0' }}>
      {toast && toast.type !== 'success' && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      {loadingDetails && (
        <Loader
          message={
            loadingAction === 'save'
              ? 'Saving employee...'
              : loadingAction === 'update'
                ? 'Updating employee...'
                : loadingAction === 'submit'
                  ? 'Submitting employee...'
                  : 'Fetching employee details...'
          }
        />
      )}
      <div className="app">
        <div className="hdr">
          <div className="hdr-l">
            <div className="hdr-ico">
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zm-4 6c-4.42 0-8 1.79-8 4v2h16v-2c0-2.21-3.58-4-8-4z"
                  fill="#fff"
                />
              </svg>
            </div>
            <div>
              <div className="d-flex align-items-center fs-5 fw-semibold">
                Employee Management
              </div>
              <div className="hdr-s">
                Create, search, edit and manage employee records
              </div>
            </div>
          </div>
          <div className="hdr-r">
            {emp.empCode && mode === 'view' && (
              <div className="emp-chip">
                <div className="emp-chip-av">
                  {emp.photo ? (
                    <img
                      src={
                        emp.photo.startsWith('data:image')
                          ? emp.photo
                          : getEmployeeFilePath(emp.photo)
                      }
                      alt="Avatar"
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <>
                      {emp.firstName?.[0]}
                      {emp.lastName?.[0]}
                    </>
                  )}
                </div>
                <span className="emp-chip-name">
                  {emp.firstName} {emp.lastName}
                </span>
                <span className="emp-chip-code">{emp.empCode}</span>
              </div>
            )}
            {emp.empCode && mode === 'view' && (
              <span className={`pill pill-${mode}`}>
                {getPostStatusLabel(emp.postStatus)}
              </span>
            )}
          </div>
        </div>

        <div className="tbar">
          {showBackButton && (
            <button
              type="button"
              className="btn btn-link p-0 back-btn fs-5"
              onClick={() => navigate(-1)}
            >
              <Icon path={mdiChevronLeft} size={1.1} />
              <span className="d-flex flex-row gap-2 fs-5">Back</span>
            </button>
          )}
          {!isReadOnlyDeepLink && (
            <button
              type="button"
              className="custom-btn"
              onClick={doNew}
              disabled={isReadOnlyDeepLink}
            >
              <span>New Employee</span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
              >
                <path
                  d="M12 5v14m-7-7h14"
                  stroke="#fff"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
          {!isReadOnlyDeepLink && (
            <button
              type="button"
              className={`btn em-btn em-btn-search${
                mode === 'edit' ? ' em-btn-muted' : ''
              }`}
              onClick={() => {
                if (isReadOnlyDeepLink || isSubmittedPost) {
                  setToast({
                    message: isReadOnlyDeepLink
                      ? 'Search is not available in this view. Use Employee Master from the menu to search.'
                      : 'Search is not available while viewing a submitted employee. Use Employee Master from the menu to search.',
                    type: 'info',
                  });
                  return;
                }
                setSearch(true);
              }}
              disabled={isSubmittedPost}
            >
              {/* <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="11"
                  cy="11"
                  r="7"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="m20 20-3.5-3.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg> */}
              Search
            </button>
          )}
          {!isReadOnlyDeepLink && (
            <>
              <div className="sep" />
              <button
                type="button"
                className={
                  mode === 'edit' ? 'custom-btn' : 'custom-secondary-btn'
                }
                onClick={doEdit}
                disabled={
                  mode !== 'view' ||
                  !emp.empCode ||
                  emp.postStatus !== '0' ||
                  isReadOnly
                }
              >
                <EditIcon /> Edit
              </button>
              <button
                type="button"
                className="em-toolbar-delete-btn"
                onClick={() => {
                  if (!emp.empCode) {
                    setToast({
                      message: 'Search and select an employee to continue.',
                      type: 'info',
                    });
                    return;
                  }
                  setConfirm(true);
                }}
                disabled={
                  mode !== 'view' ||
                  !emp.empCode ||
                  emp.postStatus !== '0' ||
                  isReadOnly
                }
              >
                <DeleteIcon color="currentColor" /> Delete
              </button>
              <div className="sep" />
            </>
          )}
          <div className="dd-wrap" ref={moreRef}>
            <button
              className="btn em-btn-more"
              onClick={() => {
                if (isMoreActionsDisabled) return;
                setShowMore(p => !p);
              }}
              disabled={isMoreActionsDisabled}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="5" r="1.5" fill="currentColor" />
                <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                <circle cx="12" cy="19" r="1.5" fill="currentColor" />
              </svg>
              More Actions
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {showMore && (
              <div className="dd-menu">
                {SUB_SCREENS.map(s => (
                  <button
                    key={s.key}
                    className="dd-item"
                    onClick={() => openSubScreen(s.key)}
                  >
                    <span className="dd-icon">{s.icon}</span>
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="main">
          {/* {mode !== 'view' && (
            <div className="ib">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#0c4a6e"
                  strokeWidth="2"
                />
                <path
                  d="M12 8v4m0 4h.01"
                  stroke="#0c4a6e"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              Fields marked with{' '}
              <strong style={{ margin: '0 4px', color: 'var(--red)' }}>
                *
              </strong>{' '}
              are mandatory and must be completed before saving.
            </div>
          )} */}
          <div className="tabs">
            {tabs.map((t, i) => (
              <button
                key={t}
                className={`tabtn${tab === i ? ' on' : ''}`}
                onClick={() => setTab(i)}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="tpanel">
            {tab === 0 && (
              <PersonalDataTab d={emp} ch={ch} off={off} opts={opts} />
            )}
            {tab === 1 && (
              <OfficialDetailsTab d={emp} ch={ch} off={off} opts={opts} />
            )}
            {tab === 2 && (
              <ContactOtherTab d={emp} ch={ch} off={off} opts={opts} />
            )}
            {mode !== 'view' && (
              <div className="factions factions-wfh-actions">
                <div className="factions-apply-leave-inner">
                  {mode === 'create' && (
                    <button
                      type="button"
                      className="custom-light-btn"
                      onClick={doCancel}
                    >
                      Clear
                    </button>
                  )}
                  {(mode === 'create' || emp.postStatus === '0') && (
                    <button
                      type="button"
                      className="custom-secondary-btn"
                      onClick={doSave}
                    >
                      <span>Save</span>
                    </button>
                  )}
                  {mode === 'edit' && emp.postStatus === '0' && (
                    <button
                      type="button"
                      className="custom-btn"
                      onClick={doSubmit}
                    >
                      <span>Submit</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {subScreen && (
          <SubScreen
            screen={subScreen}
            empName={`${emp.firstName} ${emp.lastName}`}
            empCode={emp.empCode}
            opts={opts}
            fetchFacilityCategories={fetchFacilityCategories}
            readOnly={isReadOnly}
            onClose={() => setSubScreen(null)}
          />
        )}
        {search && (
          <SearchModal
            onPick={handlePickEmployee}
            onClose={() => setSearch(false)}
          />
        )}
        {confirm && (
          <ConfirmModal
            msg={`Are you sure you want to delete ${emp.firstName} ${emp.lastName} (${emp.empCode})? This action cannot be undone.`}
            onYes={doDelete}
            onNo={() => setConfirm(false)}
            loading={isDeleting}
          />
        )}
      </div>
    </div>
  );
}
